import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { SubscriptionStatusBadge } from '@/modules/subscriptions/components/SubscriptionStatusBadge';
import { EntitlementStatusBadge } from '@/modules/trials/components/EntitlementStatusBadge';
import { TrialTruncatedText } from '@/modules/trials/components/TrialTruncatedText';
import { Button } from '@/shared/components/ui/button';
import { Icons } from '@/shared/components/ui/icons';
import { formatMoney } from '@/shared/utils/money';
import { InstapayReceiptInlinePreview } from './InstapayReceiptInlinePreview';
import { PaymentProviderBadge } from './PaymentProviderBadge';
import { PaymentStatusBadge } from './PaymentStatusBadge';
import {
  approveInstapayPayment,
  rejectInstapayPayment,
} from '../services/adminInstapayReviews.api';
import type { AdminInstapayReviewItem } from '../types/adminInstapayReview';
import { formatDateTime } from '../utils/paymentHelpers';

type DialogMode = 'approve' | 'reject' | null;

interface InstapayReviewDetailDrawerProps {
  review: AdminInstapayReviewItem | null;
  locale: string;
  canReview: boolean;
  onClose: () => void;
  onUpdated: (item: AdminInstapayReviewItem) => void;
}

function formatTempAccessRemaining(
  seconds: number | null,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  if (seconds == null || seconds <= 0) return t('payments.instapay.tempAccess.expired');
  const minutes = Math.ceil(seconds / 60);
  return t('payments.instapay.tempAccess.remaining', { minutes });
}

export function InstapayReviewDetailDrawer({
  review,
  locale,
  canReview,
  onClose,
  onUpdated,
}: InstapayReviewDetailDrawerProps) {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState<DialogMode>(null);
  const [reason, setReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const approveMutation = useMutation({
    mutationFn: () =>
      approveInstapayPayment(review!.payment_id, {
        reason: reason.trim(),
        version: review!.review_version,
      }),
    onSuccess: (item) => {
      setDialog(null);
      setReason('');
      setActionError(null);
      onUpdated(item);
    },
    onError: () => setActionError(t('payments.instapay.errors.approveFailed')),
  });

  const rejectMutation = useMutation({
    mutationFn: () =>
      rejectInstapayPayment(review!.payment_id, {
        reason: reason.trim(),
        version: review!.review_version,
      }),
    onSuccess: (item) => {
      setDialog(null);
      setReason('');
      setActionError(null);
      onUpdated(item);
    },
    onError: () => setActionError(t('payments.instapay.errors.rejectFailed')),
  });

  const isPending = approveMutation.isPending || rejectMutation.isPending;

  const disabledReason = useMemo(() => {
    if (!review) return null;
    if (!canReview) return t('payments.instapay.actions.disabled.noPermission');
    if (!review.reviewable) return t('payments.instapay.actions.disabled.notReviewable');
    return null;
  }, [review, canReview, t]);

  if (!review) return null;

  const unavailable = t('payments.identity.unavailable');

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/40" aria-hidden onClick={onClose} />
      <aside
        className="fixed inset-y-0 end-0 z-50 flex w-full max-w-lg flex-col border-s border-border bg-card shadow-xl"
        aria-label={t('payments.instapay.drawer.title')}
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <p className="text-xs text-muted-foreground">{t('payments.instapay.drawer.eyebrow')}</p>
            <h2 className="text-lg font-semibold">{t('payments.instapay.drawer.title')}</h2>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label={t('payments.instapay.drawer.close')}
          >
            <Icons.X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          <section className="space-y-3">
            <h3 className="text-sm font-semibold">{t('payments.identity.sectionTitle')}</h3>
            <dl className="grid gap-2 text-sm">
              <div>
                <dt className="text-muted-foreground">{t('payments.identity.company')}</dt>
                <dd>{review.tenant_name}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t('payments.identity.customerName')}</dt>
                <dd>
                  <TrialTruncatedText
                    value={review.customer_name}
                    fallback={unavailable}
                    maxWidthClass="max-w-full"
                  />
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t('payments.identity.customerEmail')}</dt>
                <dd>{review.customer_email ?? unavailable}</dd>
              </div>
            </dl>
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold">{t('payments.instapay.sections.payment')}</h3>
            <div className="flex flex-wrap items-center gap-2">
              <PaymentProviderBadge provider={review.provider} label={review.provider_label} />
              <PaymentStatusBadge status={review.status} />
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                {t(
                  `payments.instapay.reviewStatus.${review.payment_status}`,
                  review.payment_status,
                )}
              </span>
            </div>
            <p className="text-lg font-semibold">{formatMoney(review.amount, review.currency)}</p>
            <dl className="grid gap-2 text-sm">
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">
                  {t('payments.columns.subscriptionStatus')}
                </dt>
                <dd>
                  <SubscriptionStatusBadge status={review.subscription_status} />
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">{t('payments.columns.entitlementStatus')}</dt>
                <dd>
                  <EntitlementStatusBadge status={review.entitlement_status} />
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">{t('payments.instapay.tempAccess.label')}</dt>
                <dd>
                  {review.temporary_access_active
                    ? formatTempAccessRemaining(review.temporary_access_remaining_seconds, t)
                    : t('payments.instapay.tempAccess.expired')}
                </dd>
              </div>
            </dl>
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold">{t('payments.instapay.sections.receipt')}</h3>
            {review.receipt_preview_url ? (
              <InstapayReceiptInlinePreview
                previewUrl={review.receipt_preview_url}
                fileName={review.receipt_file_name}
                mimeType={review.receipt_mime_type}
              />
            ) : (
              <div className="rounded-lg border border-dashed border-border bg-muted/20 p-4 text-sm text-muted-foreground">
                {t('payments.instapay.receipt.previewUnavailable')}
              </div>
            )}
            <dl className="grid gap-2 text-sm">
              <div>
                <dt className="text-muted-foreground">{t('payments.instapay.receipt.fileName')}</dt>
                <dd>{review.receipt_file_name ?? unavailable}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t('payments.instapay.receipt.mimeType')}</dt>
                <dd>{review.receipt_mime_type ?? unavailable}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t('payments.instapay.receipt.size')}</dt>
                <dd>
                  {review.receipt_size_bytes != null
                    ? t('payments.instapay.receipt.sizeValue', {
                        bytes: review.receipt_size_bytes.toLocaleString(locale),
                      })
                    : unavailable}
                </dd>
              </div>
            </dl>
          </section>

          {review.rejection_reason ? (
            <section className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
              <p className="font-medium text-destructive">
                {t('payments.instapay.rejectionReason')}
              </p>
              <p className="mt-1 text-foreground">{review.rejection_reason}</p>
            </section>
          ) : null}

          {actionError ? (
            <p className="text-sm text-destructive" role="alert">
              {actionError}
            </p>
          ) : null}
        </div>

        <div className="border-t border-border p-4 space-y-2">
          {disabledReason ? (
            <p className="text-xs text-muted-foreground">{disabledReason}</p>
          ) : null}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Button
              variant="outline"
              disabled={Boolean(disabledReason) || isPending}
              onClick={() => {
                setReason('');
                setActionError(null);
                setDialog('approve');
              }}
            >
              {t('payments.instapay.actions.approve')}
            </Button>
            <Button
              variant="destructive"
              disabled={Boolean(disabledReason) || isPending}
              onClick={() => {
                setReason('');
                setActionError(null);
                setDialog('reject');
              }}
            >
              {t('payments.instapay.actions.reject')}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            {t('payments.instapay.drawer.updatedAt', {
              time: formatDateTime(review.updated_at, locale),
            })}
          </p>
        </div>
      </aside>

      {dialog ? (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 p-4">
          <div
            role="dialog"
            aria-modal="true"
            className="w-full max-w-md space-y-4 rounded-xl border border-border bg-card p-5 shadow-xl"
          >
            <h3 className="text-lg font-semibold">
              {dialog === 'approve'
                ? t('payments.instapay.dialogs.approve.title')
                : t('payments.instapay.dialogs.reject.title')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {dialog === 'approve'
                ? t('payments.instapay.dialogs.approve.description')
                : t('payments.instapay.dialogs.reject.description')}
            </p>
            <div className="space-y-1.5">
              <label htmlFor="instapay-review-reason" className="text-sm">
                {t('payments.instapay.dialogs.reasonRequired')}
              </label>
              <input
                id="instapay-review-reason"
                type="text"
                className="flex h-10 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={() => setDialog(null)} disabled={isPending}>
                {t('common.cancel')}
              </Button>
              <Button
                variant={dialog === 'reject' ? 'destructive' : 'default'}
                isLoading={
                  dialog === 'approve' ? approveMutation.isPending : rejectMutation.isPending
                }
                disabled={reason.trim().length === 0}
                onClick={() => {
                  if (dialog === 'approve') approveMutation.mutate();
                  else rejectMutation.mutate();
                }}
              >
                {dialog === 'approve'
                  ? t('payments.instapay.dialogs.approve.confirm')
                  : t('payments.instapay.dialogs.reject.confirm')}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
